/**
 * OpenAPI 자동생성에 포함되지 않는 인증/세션 관련 수동 API 호출.
 *
 * `APIClient`가 아닌 `axiosInstance`를 사용한다.
 * axiosInstance는 401 응답 시 토큰 자동 갱신 인터셉터가 포함되어 있어,
 * 세션 체크 중 토큰 만료가 발생해도 자동으로 재시도한다.
 *
 * ★ `sessionStorage`에 직접 접근하므로 SSR/정적 export prerender 맥락에서 호출하면
 * `sessionStorage is not defined` 오류가 발생한다. 반드시 클라이언트 사이드에서만 호출한다.
 */
import api from "pages/api/axiosInstance";
import { getApiBaseUrl } from "@/utils/getApiBaseUrl";

export interface CheckSessionResponse {
    isSuccess: boolean;
    code: number;
    message: string;
    result: boolean;
}

/**
 * 현재 세션이 유효한지 확인한다. `POST /app/auths/check-session`
 *
 * sessionStorage에 토큰이 없으면 BE 호출 없이 즉시 throw한다.
 * `useSessionHeartbeat` 훅이 15초 주기로 이 함수를 호출한다.
 */
export const checkSession = async (): Promise<CheckSessionResponse> => {
    if (!sessionStorage.getItem("accessToken")) {
        throw new Error("No access token");
    }

    const response = await api.post<CheckSessionResponse>(
        `/app/auths/check-session`
    );

    return response.data;
};

/**
 * 탭 종료(beforeunload) 시 세션 TTL을 40초로 축소한다. `PATCH /app/auths/session/shrink`
 *
 * 세션 자체를 삭제하지 않고 TTL만 줄여서, 40초 내 새로고침/복귀 시 세션을 유지할 수 있다.
 */
export const shrinkSession = async (): Promise<CheckSessionResponse> => {
    const response = await api.patch<CheckSessionResponse>(
        `/app/auths/session/shrink`
    );
    return response.data;
};

/**
 * 새로고침/탭 복귀 시 세션 TTL을 3분으로 복구한다. `PATCH /app/auths/session/restore`
 *
 * `shrinkSession`으로 축소된 TTL을 원래 값으로 되돌린다.
 */
export const restoreSession = async (): Promise<CheckSessionResponse> => {
    const response = await api.patch<CheckSessionResponse>(
        `/app/auths/session/restore`
    );
    return response.data;
};
